import { AnimatePresence, motion as m } from 'framer-motion'
import { useTranslation } from 'react-i18next'
import { useLocation, useNavigate } from 'react-router-dom'

import pointOfSalesSvg from '@/assets/icons/point-of-sales.svg'
import settiSvg from '@/assets/icons/setti.svg'
import { useGetCompanyByAlphaId } from '@/shared/hooks/queries/useGetCompanyByAlphaId'

import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../ui/accordion'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
} from '../ui/dropdown-menu'
import { CollapsibleItem } from './collapsible-item'
import { DropdownMenuTrigger } from './dropdown-menu-trigger'

interface PointOfSalesMenuOptionProps {
  posId: string
  agencyId: string
  opened: boolean
}

export function PointOfSalesMenuOption({
  posId,
  agencyId,
  opened,
}: PointOfSalesMenuOptionProps) {
  const { pathname } = useLocation()
  const { t } = useTranslation('sidebar')
  const navigate = useNavigate()
  const { data: pointOfSales, isLoading } = useGetCompanyByAlphaId(posId)
  const name = pointOfSales?.legalData.name ?? 'Ponto de Vendas'

  return (
    <>
      <AnimatePresence>
        {opened && (
          <Accordion defaultValue="point-of-sales" collapsible type="single">
            <AccordionItem
              className="data-[state=closed]:border-none group"
              value="point-of-sales"
            >
              <AccordionTrigger
                hideCloseButton
                className="bg-[#3E54C0] enabled:hover:brightness-110 h-10 rounded-md duration-200 w-full px-2 flex items-center gap-2 mb-0 data-[state=open]:brightness-110 "
              >
                <img
                  className="size-6"
                  src={pointOfSalesSvg}
                  alt="Point of Sales"
                />

                <AnimatePresence>
                  {opened && (
                    <m.span
                      initial={{ y: 20, opacity: 0 }}
                      exit={{
                        y: 20,
                        opacity: 0,
                        transition: { duration: 0.2 },
                      }}
                      animate={{
                        y: 0,
                        opacity: 1,
                        transition: { duration: 0.2, delay: 0.2 },
                      }}
                      className="flex-1 text-left line-clamp-1"
                    >
                      {isLoading ? 'Carregando...' : name}
                    </m.span>
                  )}
                </AnimatePresence>

                <img
                  className="size-2 rotate-90 group-data-[state=open]:rotate-[270deg] duration-300"
                  src={settiSvg}
                  alt="Setti"
                />
              </AccordionTrigger>

              <AccordionContent className="text-base -mt-1 -mb-2">
                <CollapsibleItem
                  to={`/agency/${agencyId}/point-of-sales/${posId}/config`}
                >
                  {t('--menu-options-configurations')}
                </CollapsibleItem>

                <CollapsibleItem
                  to={`/agency/${agencyId}/point-of-sales/${posId}/details`}
                >
                  {t('--menu-options-details')}
                </CollapsibleItem>

                <CollapsibleItem
                  to={`/agency/${agencyId}/point-of-sales/${posId}/policies`}
                  isActive={pathname.includes(
                    `/agency/${agencyId}/point-of-sales/${posId}/policies`,
                  )}
                >
                  {t('--menu-options-purchase-policies')}
                </CollapsibleItem>
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {!opened && (
          <DropdownMenu>
            <DropdownMenuTrigger>
              <img
                className="size-6"
                src={pointOfSalesSvg}
                alt="PointOfSales"
              />
            </DropdownMenuTrigger>

            <DropdownMenuContent
              side="right"
              sideOffset={24}
              align="start"
              className="bg-primaryDark border-none p-2 rounded-[5px]"
            >
              <DropdownMenuItem
                onClick={() =>
                  navigate(
                    `/agency/${agencyId}/point-of-sales/${posId}/policies`,
                  )
                }
                className="px-4 text-white hover:bg-white/10 rounded-md"
              >
                {t('--menu-options-purchase-policies')}
              </DropdownMenuItem>

              <DropdownMenuItem
                onClick={() =>
                  navigate(
                    `/agency/${agencyId}/point-of-sales/${posId}/details`,
                  )
                }
                className="px-4 text-white hover:bg-white/10 rounded-md"
              >
                {t('--menu-options-details')}
              </DropdownMenuItem>

              <DropdownMenuItem
                onClick={() =>
                  navigate(`/agency/${agencyId}/point-of-sales/${posId}/config`)
                }
                className="px-4 text-white hover:bg-white/10 rounded-md"
              >
                {t('--menu-options-configurations')}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </AnimatePresence>
    </>
  )
}
